import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Search, Loader2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Settings } from "lucide-react";

// Helper component to fetch and display progress for a single user
const UserDelegationCard = ({ user }: { user: any }) => {
    const { token } = useAuth();
    const [stats, setStats] = useState({
        direct: 0,
        coach: 0,
        support: 0,
        delegate: 0,
        total: 0,
        progress: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDelegations = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation/user/${user.id}`, {
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });
                const data = await response.json();
                if (data.success) {
                    const direct = data.delegations.filter((d: any) => d.competenceLevel === 'Direct').length;
                    const coach = data.delegations.filter((d: any) => d.competenceLevel === 'Coach').length;
                    const support = data.delegations.filter((d: any) => d.competenceLevel === 'Support').length;
                    const delegate = data.delegations.filter((d: any) => d.competenceLevel === 'Delegate').length;
                    const total = data.delegations.length;

                    // Progress: (Coach + Support + Delegate) / Total * 100 ?? 
                    // Or weighted? Screenshot says "100% of tasks at Coach level or above"
                    // Let's assume progress is based on "Delegate" being goal.
                    // Screenshot: "100% of tasks at Coach level or above" implies Count(>=Coach) / Total.

                    const aboveDirect = coach + support + delegate;
                    const progress = total > 0 ? Math.round((aboveDirect / total) * 100) : 0;

                    setStats({ direct, coach, support, delegate, total, progress });
                }
            } catch (error) {
                console.error("Error fetching delegation stats for user", user.id, error);
            } finally {
                setLoading(false);
            }
        };

        fetchDelegations();
    }, [user.id, token]);

    return (
        <Link to={`/development/${user.id}`}>
            <Card className="hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors cursor-pointer h-full">
                <CardContent className="p-6 space-y-4">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 bg-slate-200">
                                <AvatarFallback>{user.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div>
                                <h3 className="font-semibold text-lg hover:underline">{user.name}</h3>
                                <p className="text-sm text-muted-foreground">Team Member</p>
                            </div>
                        </div>
                        {user.jobRole && <Badge variant="secondary">{user.jobRole.substring(0, 3).toUpperCase()}</Badge>}
                    </div>

                    <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Delegation Tasks</span>
                            <span className="font-medium">{loading ? '-' : stats.total}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Progression</span>
                            <span className="font-medium">{loading ? '-' : `${stats.progress}%`}</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-black dark:bg-white rounded-full transition-all duration-500"
                                style={{ width: `${stats.progress}%` }}
                            ></div>
                        </div>
                        <p className="text-xs text-muted-foreground">Coach level or above</p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-4 gap-2 pt-2">
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-red-500 mb-1">🎯</div>
                            <div className="font-bold text-sm">{stats.direct}</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-orange-500 mb-1">🧭</div>
                            <div className="font-bold text-sm">{stats.coach}</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-yellow-500 mb-1">🤝</div>
                            <div className="font-bold text-sm">{stats.support}</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-green-500 mb-1">🏁</div>
                            <div className="font-bold text-sm">{stats.delegate}</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
};

export default function DevelopmentPage() {
    const { users } = useData();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");

    // Filter users to show only those I manage + myself? Or just everyone if I have permission?
    // User Routes logic:
    // - Admin/Director can read all
    // - Manager can read managed
    // `users` from useData should already be filtered by the backend if I'm not allowed to see all.
    // BUT `useData` calls `api/users` which respects permissions.
    // So I can just map `users`.

    const filteredUsers = users.filter(user =>
        user.isActive !== false &&
        user.archived !== true && // Only active and not archived
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-2xl font-bold">Development</h1>
                    <p className="text-muted-foreground">
                        Map, monitor, and grow your team's competence through structured delegation.
                    </p>
                </div>
                <Button variant="outline" onClick={() => navigate('/development/settings')}>
                    <Settings className="mr-2 h-4 w-4" /> Settings
                </Button>
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Search team members..."
                    className="pl-10 max-w-xl"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredUsers.map(user => (
                    <UserDelegationCard key={user.id} user={user} />
                ))}
                {filteredUsers.length === 0 && (
                    <div className="col-span-full text-center py-10 text-muted-foreground">
                        No team members found.
                    </div>
                )}
            </div>
        </div>
    );
}
